import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { toast } from "sonner";

import { clearAccessToken, getAccessToken, refreshAccessToken } from "@/lib/auth/token-store";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // Required so the HttpOnly refresh cookie scoped to .homewoodcreations.com
  // rides cross-subdomain XHR (oms2 → api).
  withCredentials: true,
});

// Request interceptor: attach the in-memory JWT as a Bearer token. When it's
// empty (e.g. the moment after a reload, before bootstrapSession's silent
// refresh completes) the request goes out unauthenticated and the 401 handler
// below drives the refresh + retry.
apiClient.interceptors.request.use((config) => {
  const access = getAccessToken();
  if (access) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

// Shape of every non-2xx JSON response — pinned at the backend by
// ``oms/common/exception_handler.py``. See ``docs/backend-conventions.md``
// § Errors.
export interface ApiErrorEnvelope {
  code: string;
  title: string;
  message: string;
  detail: string;
  errors?: Record<string, string[]>;
  extra?: {
    timer?: number;
    wait?: number;
    skip_common_handling?: boolean;
    transaction_id?: string;
    [key: string]: unknown;
  };
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

// Error handling interceptor — also drives the silent-refresh-on-401 dance.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config as RetryableRequestConfig | undefined;
    let data = error.response?.data as ApiErrorEnvelope | Blob | undefined;

    // Blob endpoints (responseType: "blob") deliver JSON error envelopes
    // as a Blob too — parse it back so the toast logic below sees the
    // standard shape.
    if (data instanceof Blob && data.type.includes("json")) {
      try {
        data = JSON.parse(await data.text()) as ApiErrorEnvelope;
        if (error.response) {
          error.response.data = data;
        }
      } catch {
        data = undefined;
      }
    }

    const envelope = data as ApiErrorEnvelope | undefined;

    // 401: try silent refresh once before redirecting. The refresh endpoint
    // itself returns 401 when the cookie is missing/expired — short-circuit
    // there so we don't loop.
    if (status === 401 && config && !config._retried && !config.url?.includes("/api/users/token/refresh/")) {
      config._retried = true;
      const newAccess = await refreshAccessToken();
      if (newAccess) {
        config.headers.Authorization = `Bearer ${newAccess}`;
        return apiClient(config);
      }
      // Refresh failed — purge state and bounce to login.
      clearAccessToken();
      window.location.href = "/login";
      return Promise.reject(error);
    }

    if (status === 500) {
      const transactionId = envelope?.extra?.transaction_id;
      toast.error("Internal Error", {
        description: transactionId
          ? `An error occurred. Please contact support with transaction ID: ${transactionId}`
          : "An unexpected error occurred. Please try again.",
      });
    }

    // 4xx — show toast unless the envelope asks us to skip.
    if (
      status !== undefined &&
      status >= 400 &&
      status < 500 &&
      status !== 401 &&
      envelope?.code &&
      !envelope.extra?.skip_common_handling
    ) {
      const description = envelope.message;
      if (envelope.extra?.timer) {
        toast.error(envelope.title, {
          description,
          duration: envelope.extra.timer,
        });
      } else {
        toast.error(envelope.title, { description });
      }
    }

    return Promise.reject(error);
  },
);

export async function logError(data: Record<string, unknown>) {
  const access = getAccessToken();
  return axios.post(`${API_BASE_URL}/api/core/log/error/`, data, {
    withCredentials: true,
    headers: access ? { Authorization: `Bearer ${access}` } : {},
  });
}
